import type { EntityPluginContext, GeneratedContent } from "@brains/plugins";
import { BaseGenerationJobHandler } from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import type { ProgressReporter } from "@brains/utils/progress";
import { z } from "@brains/utils/zod";
import { generationResultSchema } from "@brains/contracts";
import { fetchAgentCard, extractDomain } from "../lib/fetch-agent-card";
import { buildAgentFromCard } from "../lib/build-agent-content";
import { AGENT_ENTITY_TYPE } from "../lib/constants";
type AgentGenerationStatusSchema = z.ZodEnum<{
  discovered: "discovered";
  approved: "approved";
  archived: "archived";
}>;

const agentGenerationStatusSchema: AgentGenerationStatusSchema = z.enum([
  "discovered",
  "approved",
  "archived",
]);

/**
 * Input schema for agent generation — just needs a URL/domain.
 */
type AgentGenerationJobSchema = z.ZodObject<{
  prompt: z.ZodOptional<z.ZodString>;
  url: z.ZodOptional<z.ZodString>;
  content: z.ZodOptional<z.ZodString>;
  skipAi: z.ZodOptional<z.ZodBoolean>;
  status: z.ZodOptional<AgentGenerationStatusSchema>;
}>;

export const agentGenerationJobSchema: AgentGenerationJobSchema = z.object({
  prompt: z.string().optional(),
  url: z.string().optional(),
  content: z.string().optional(),
  skipAi: z.boolean().optional(),
  status: agentGenerationStatusSchema.optional(),
});

export type AgentGenerationJobData = z.output<typeof agentGenerationJobSchema>;

export const agentGenerationResultSchema: ReturnType<
  typeof generationResultSchema.extend<{
    name: z.ZodOptional<z.ZodString>;
    domain: z.ZodOptional<z.ZodString>;
  }>
> = generationResultSchema.extend({
  name: z.string().optional(),
  domain: z.string().optional(),
});

export type AgentGenerationResult = z.output<
  typeof agentGenerationResultSchema
>;

/**
 * Generation handler for agent entities.
 * Fetches an Agent Card from the given URL/domain, parses anchor info,
 * and creates a rich agent entity with skills and about sections.
 *
 * No AI needed — all content comes from the Agent Card.
 */
export class AgentGenerationJobHandler extends BaseGenerationJobHandler<
  AgentGenerationJobData,
  AgentGenerationResult
> {
  constructor(logger: Logger, context: EntityPluginContext) {
    super(logger, context, {
      schema: agentGenerationJobSchema,
      jobTypeName: "agent-generation",
      entityType: AGENT_ENTITY_TYPE,
    });
  }

  protected async generate(
    data: AgentGenerationJobData,
    _progressReporter: ProgressReporter,
  ): Promise<GeneratedContent> {
    // Extract domain from prompt or explicit url field
    const rawUrl = data.url ?? data.prompt ?? "";
    const domain = extractDomain(rawUrl);

    if (!domain) {
      this.failEarly(
        "No URL or domain provided. Use: system_create agent with a domain like yeehaa.io",
      );
    }

    const card = await fetchAgentCard(domain, globalThis.fetch);
    if (!card) {
      this.failEarly(
        `Could not fetch Agent Card from ${domain}. Make sure the agent is running and accessible.`,
      );
    }

    const { content, metadata, anchorName } = buildAgentFromCard(card, {
      ...(data.status !== undefined ? { status: data.status } : {}),
    });

    return {
      id: domain,
      content,
      metadata,
      title: anchorName,
    };
  }
}
